import {
  ABILITY_MESSAGE_BOARD,
  ABILITY_ENSUI_JUTEN_KARADANPU_INPUT,
  ABILITY_ENSUI_PLANT_STOCK_TABLE,
  ABILITY_ENSUI_JUTEN_KARADANPU_MAP,
  ABILITY_HAISETSU_KEISAN_INPUT,
  ABILITY_PROGRESS_TABLE,
  ABILITY_SAGYOU_JOUKYOU_INPUT,
  ABILITY_SAGYOU_JOUKYOU_MAP,
  ABILITY_SEKISETSU_INPUT,
  ABILITY_SETTOU_REPORT_LIST,
  ABILITY_SETTOU_PATROL_REPORT_INPUT,
  ABILITY_SETTOU_ONSITE_MANAGEMENT_REPORT_INPUT,
  ABILITY_CLEANING_MAP,
  ABILITY_DOMAIN_JOHAISETSU,
  ABILITY_DOMAIN_CLEANING,
  ABILITY_TARGET_USER_TYPE_OFFICE,
  ABILITY_TARGET_USER_TYPE_DRIVER,
} from '@/consts/ability';
import { AdminAbility } from '@/models/apis/ability/adminAbilityResponse';

import { isRoleHonsha, isRoleKyoku, isDefaultRole } from '@/lib/roleHelper';
import { isJohaisetsuRoleHonsha } from '@/lib/johaisetsu/johaisetsuRoleHelper';
import { isAnyDriver } from '@/lib/userAffiliation';
import { UserState } from '@/store/modules/user';
import { Route } from 'vue-router';
import { getRedirectDefaultPageUrl } from '@/lib/defaultPageHelper';

export const filterJohaisetsuAbilities = (abilities: AdminAbility[]): AdminAbility[] => {
  return abilities.filter(e => e.domain === ABILITY_DOMAIN_JOHAISETSU);
};
export const filterCleaningAbilities = (abilities: AdminAbility[]): AdminAbility[] => {
  return abilities.filter(e => e.domain === ABILITY_DOMAIN_CLEANING);
};
// 協力会社単位で付与する、雪凍のドライバー向け ability
export const filterJohaisetsuDriverAbilities = (abilities: AdminAbility[]): AdminAbility[] => {
  return filterJohaisetsuAbilities(abilities).filter(e => e.target_user_type === ABILITY_TARGET_USER_TYPE_DRIVER);
};

export const getAbilityIdsForDisp = (abilities: AdminAbility[], type: 'cleaning' | 'johaisetsu'): string => {
  if (type === 'cleaning') {
    return filterCleaningAbilities(abilities).map(e => e.id).join(', ');
  }
  return filterJohaisetsuAbilities(abilities).map(e => e.id).join(', ');
};

export function filterAbilitiesByJohaisetsuRole(abilitiesOrig: AdminAbility[], role: string | null): AdminAbility[] {
  if (!role) { return []; }

  const abilities = filterJohaisetsuAbilities(abilitiesOrig);
  if (isAnyDriver(role)) {
    // ドライバーの作業権限は協力会社単位で設定するため、個人単位では掲示板のみ選択可能
    return abilities.filter(e => e.id === ABILITY_MESSAGE_BOARD);
  }

  return abilities.filter(e => e.target_user_type === ABILITY_TARGET_USER_TYPE_OFFICE);
}

export function filterAbilitiesByCleaningRole(abilitiesOrig: AdminAbility[], role: string | null): AdminAbility[] {
  let ret: AdminAbility[] = [];
  if (!role) { return ret; }

  const abilities = filterCleaningAbilities(abilitiesOrig);
  if (
    isRoleHonsha(role) ||
    isRoleKyoku(role) ||
    isDefaultRole(role)
  ) {
    ret = abilities.filter(e => e.target_user_type === ABILITY_TARGET_USER_TYPE_OFFICE);
  } else if (isAnyDriver(role)) {
    ret = abilities.filter(e => e.target_user_type === ABILITY_TARGET_USER_TYPE_DRIVER);
  }

  return ret;
}

const routeNameToRequiredAbilityName: Record<string, number> = {
  JohaisetsuEnsuiSanpuJoukyouInput: ABILITY_ENSUI_JUTEN_KARADANPU_INPUT,
  JohaisetsuJutenSanpuJoukyouInput: ABILITY_ENSUI_JUTEN_KARADANPU_INPUT,
  EnsuiPlantStockTable: ABILITY_ENSUI_PLANT_STOCK_TABLE,
  JohaisetsuEnsuiSanpuJoukyouMap: ABILITY_ENSUI_JUTEN_KARADANPU_MAP,
  JohaisetsuJutenSanpuJoukyouMap: ABILITY_ENSUI_JUTEN_KARADANPU_MAP,
  JohaisetsuHaisetsuKeisanInput: ABILITY_HAISETSU_KEISAN_INPUT,
  JohaisetsuProgressTable: ABILITY_PROGRESS_TABLE,
  JohaisetsuSagyouJoukyouInput: ABILITY_SAGYOU_JOUKYOU_INPUT,
  JohaisetsuSagyouJoukyouMap: ABILITY_SAGYOU_JOUKYOU_MAP,
  JohaisetsuSekisetsuInput: ABILITY_SEKISETSU_INPUT,
  JohaisetsuSettouPatrolReportInput: ABILITY_SETTOU_PATROL_REPORT_INPUT,
  JohaisetsuSettouOnsiteManagementReportInput: ABILITY_SETTOU_ONSITE_MANAGEMENT_REPORT_INPUT,
  SettouPatrolList: ABILITY_SETTOU_REPORT_LIST,
  SettouPatrolDetail: ABILITY_SETTOU_REPORT_LIST,
  SettouOnsiteManagementDetail: ABILITY_SETTOU_REPORT_LIST,
  CleaningMap: ABILITY_CLEANING_MAP,
};

function isAbilityControlledPage(routeName: string): boolean {
  return Object.keys(routeNameToRequiredAbilityName).includes(routeName);
}

function isQualifiedForAbilityControlledPage(routeName: string, userState: UserState): boolean {
  const requiredAbility = routeNameToRequiredAbilityName[routeName];
  return userState.abilityMap[requiredAbility] !== undefined;
}

function isJohaisetsuTaskForcePage(routeName: string): boolean {
  return routeName === 'JohaisetsuTaskForce';
}

function isQualifiedForJohaisetsuTaskForcePage(userState: UserState): boolean {
  return isJohaisetsuRoleHonsha(userState.johaisetsu_role);
}

function isWeatherObservatoryDataPointsTablePage(routeName: string): boolean {
  return routeName === 'WeatherObservatoryDataPointsTable';
}

function isQualifiedForWeatherObservatoryDataPointsTablePage(userState: UserState): boolean {
  return userState.settings.can_use_weather_observatory;
}

function isGeneralPage(routeName: string): boolean {
  return routeName === 'Route' || routeName === 'Top';
}

export function isQualifiedForGeneralPage(userState: UserState): boolean {
  return !userState.use_johaisetsu_only &&
    !userState.use_cleaning_only &&
    !isAnyDriver(userState.johaisetsu_role) &&
    !isAnyDriver(userState.cleaning_role);
}

export function hasMessageBoardAbility(userState: UserState): boolean {
  return !!userState.abilityMap[ABILITY_MESSAGE_BOARD];
}

export function useExternalReportOnly(userState: UserState): boolean {
  return !!userState.external_report_abilities?.some(ability => ability.use_this_external_report_only);
}

export function redirectIfNoAbility(userState: UserState, route: Route): void {
  if (!route.name) {
    return;
  }

  let redirectTo = '/';
  if (isAbilityControlledPage(route.name)) {
    if (isQualifiedForAbilityControlledPage(route.name, userState)) {
      return;
    }
  } else if (isJohaisetsuTaskForcePage(route.name)) {
    if (isQualifiedForJohaisetsuTaskForcePage(userState)) {
      return;
    }
  } else if (isWeatherObservatoryDataPointsTablePage(route.name)) {
    if (isQualifiedForWeatherObservatoryDataPointsTablePage(userState)) {
      return;
    }
  } else if (isGeneralPage(route.name)) {
    if (useExternalReportOnly(userState)) {
      const useExternalReport = userState.external_report_abilities?.find(
        ability => ability.use_this_external_report_only,
      );
      if (!useExternalReport) return;
      redirectTo = `/external-map/${useExternalReport.external_report_type}`;
    } else if (isQualifiedForGeneralPage(userState)) {
      return;
    } else {
      // TopやRouteが見れないユーザーはデフォルト画面にリダイレクト
      redirectTo = getRedirectDefaultPageUrl({
        useJohaisetsuOnly: userState.use_johaisetsu_only,
        useCleaningOnly: userState.use_cleaning_only,
        defaultPage: userState.default_page,
      });
    }
  }

  location.href = redirectTo;
}
